import { z } from "zod";
import type { PossiblyIncompleteShellEnv, ShellEnvFromHTML } from "./client";

const mfeConfigSchema = z.object({
  name: z.string(),
  variant: z.string(),
  revision: z.string(),
  flags: z
    .array(z.string())
    .nullish()
    .transform((val) => val ?? []),
});

const mfeUploadServiceResponseSchema = z.object({
  mfes: z.array(mfeConfigSchema),
});

export type MfeConfig = z.infer<typeof mfeConfigSchema>;

// Const/let statements do not create globals on window, however the variables are invisibly scoped to here.
declare const SHELL_ENV: ShellEnvFromHTML;

// We copy the existing variable so we can refer to the initial config.
const SHELL_ENV_FROM_HTML: ShellEnvFromHTML = Object.assign({}, SHELL_ENV);

// Don't use this outside of this file
const windowMcc = window as unknown as {
  mfecc?: { mfe: MfeConfig };
};

export const mfeCatConfigPromise: Promise<
  z.infer<typeof mfeUploadServiceResponseSchema>
> = (async () => {
  try {
    if (windowMcc.mfecc) {
      return windowMcc.mfecc;
    }
    if (!SHELL_ENV_FROM_HTML.MFE_CAT_URL) {
      throw new Error("Can't fetch one-bucket MFEs: MFE_CAT_URL not set");
    }

    const mfeCatResponse = await fetch(
      `${SHELL_ENV_FROM_HTML.MFE_CAT_URL}/config/onesaas.json`,
      {
        method: "GET",
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore: 'priority' does not exist in type 'RequestInit'
        priority: "high",
      }
    );

    if (!mfeCatResponse.ok) {
      throw await mfeCatResponse.text();
    }

    return mfeCatResponse.json();
  } catch (error) {
    console.log(error);
    return { mfes: [] };
  }
})();

const getShellRevision = async () => {
  const shellVariantOverride =
    sessionStorage.getItem("_shellVariantOverride") || "main";

  const mfeCatConfig = await mfeCatConfigPromise;
  const shellMfeConfig = mfeCatConfig.mfes.reduce<MfeConfig | null>(
    (found, curr) => {
      if (found && found.variant === shellVariantOverride) {
        return found;
      }

      if (
        curr.name === "Shell" &&
        (curr.variant === shellVariantOverride || curr.variant === "main")
      ) {
        return curr;
      }

      return found;
    },
    null
  );
  if (!shellMfeConfig) {
    throw new Error("Shell config not found");
  }
  if (shellMfeConfig.variant !== shellVariantOverride) {
    console.error(
      `${shellVariantOverride} not found, ${shellMfeConfig.variant} used instead.`
    );
  }
  return shellMfeConfig.revision;
};

export const loadEnvOverrides = async () => {
  const revision = await getShellRevision();
  const url = `${SHELL_ENV_FROM_HTML.MFE_CAT_URL}/${revision}/dev/${SHELL_ENV_FROM_HTML.CONFIG_NAME}.json`;
  return await loadEnvFromURL(url);
};

// eslint-disable-next-line no-restricted-syntax
export const loadShellEnv: () => Promise<PossiblyIncompleteShellEnv> =
  async () => {
    const envOverrides = await loadEnvOverrides().catch((e) => {
      console.error(e);
      return null;
    });
    return updateShellEnvWith(SHELL_ENV_FROM_HTML, envOverrides);
  };

function updateShellEnvWith(...data: unknown[]) {
  const newEnv = Object.assign(
    {},
    window.SHELL_ENV ?? {},
    ...data.filter((x) => x != null)
  );
  window.SHELL_ENV = newEnv;
  // XXX: Hackily update the SHELL_ENV const scoped variable
  Object.assign(SHELL_ENV, newEnv);
  return window.SHELL_ENV;
}

/**
 * Loads the environment from a URL returning the new environment
 * @param url the URL to fetch the environment from
 */
async function loadEnvFromURL(url: string): Promise<unknown | null> {
  try {
    const response = await fetch(url);
    if (response.status === 200) {
      return response.json();
    } else {
      return null;
    }
  } catch (e) {
    console.error(e);
    return null;
  }
}

export const CONFIG_NAME = SHELL_ENV_FROM_HTML.CONFIG_NAME;
